// SPDX-License-Identifier: AGPL-3.0-or-later
import { useId } from "react";
import type { Slice } from "../lib/figures.js";
import { useI18n } from "../lib/i18n.js";

interface Props {
  slices: Slice[];
  /** Shown instead of the chart when there is nothing to draw. */
  empty?: string;
  /** Names a slice; defaults to its category name. */
  labelFor?: (id: string) => string;
  /** When given, slices with sub-categories open with a button in the legend. */
  onOpen?: (id: string) => void;
}

/** A donut of categories. The legend beside it carries the same numbers as text. */
export function Donut({ slices, empty, labelFor, onOpen }: Props) {
  const { t, money, percent, category } = useI18n();
  const summaryId = useId();
  const name = labelFor ?? category;
  const total = slices.reduce((s, x) => s + x.amount, 0);
  if (total === 0) return <p className="hint">{empty ?? t("pie.empty")}</p>;
  const r = 60;
  const c = 2 * Math.PI * r;
  let offset = 0;
  const top = slices[0]!;
  return (
    <div className="donut-wrap">
      <svg viewBox="0 0 160 160" role="img" aria-labelledby={summaryId} width="160" height="160">
        <g transform="rotate(-90 80 80)">
          {slices.map((s, i) => {
            const length = (s.amount / total) * c;
            const circle = (
              <circle
                key={s.id}
                cx="80"
                cy="80"
                r={r}
                fill="none"
                stroke={`var(--cat-${Math.min(i, 5)})`}
                strokeWidth="26"
                strokeDasharray={`${Math.max(0, length - 1.5)} ${c}`}
                strokeDashoffset={-offset}
              />
            );
            offset += length;
            return circle;
          })}
        </g>
        <text x="80" y="86" textAnchor="middle" fontSize="16" fontWeight="700" fill="var(--text)">
          {money(total, total >= 100_000)}
        </text>
      </svg>
      <div>
        <p id={summaryId} className="visually-hidden">
          {t("pie.summary", { name: name(top.id), amount: money(top.amount), percent: percent((top.amount / total) * 100, 0) })}
        </p>
        <ul className="legend">
          {slices.map((s, i) => (
            <li key={s.id}>
              <span className="swatch" style={{ background: `var(--cat-${Math.min(i, 5)})` }} aria-hidden="true" />
              {onOpen && s.deeper ? (
                <button type="button" className="link-button" onClick={() => onOpen(s.id)} aria-label={t("pie.open", { name: name(s.id) })}>
                  {name(s.id)}
                </button>
              ) : (
                <span>{name(s.id)}</span>
              )}
              <span>
                {money(s.amount)} <span className="hint">({percent((s.amount / total) * 100, 0)})</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
